import React from "react";
import {
  Play,
  UserCheck,
  Briefcase,
  CheckCircle,
  Circle,
  Loader,
  type LucideIcon,
} from "lucide-react";

export type NodeState = "start" | "mid" | "end";

export interface PipelineNode {
  label: string;
  sub?: string;
  icon?: LucideIcon;
  state: NodeState;
}

interface WorkflowPipelineProps {
  nodes: PipelineNode[];
  className?: string;
}

const stateConfig: Record<
  NodeState,
  { iconBg: string; iconColor: string; border: string }
> = {
  start: {
    iconBg: "var(--color-success-light)",
    iconColor: "var(--color-success)",
    border: "var(--color-success)",
  },
  mid: {
    iconBg: "var(--color-primary-100)",
    iconColor: "var(--color-primary-600)",
    border: "var(--color-primary-200)",
  },
  end: {
    iconBg: "var(--color-info-light)",
    iconColor: "var(--color-info)",
    border: "var(--color-info)",
  },
};

const WorkflowPipeline: React.FC<WorkflowPipelineProps> = ({
  nodes,
  className = "",
}) => {
  return (
    <div
      className={`bg-neutral-50 border border-[var(--color-border-secondary)] rounded-[18px] p-6 overflow-x-auto ${className}`}
      style={{
        backgroundImage:
          "radial-gradient(#E7E3F2 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <div className="flex items-center gap-0 min-w-max">
        {nodes.map((node, i) => {
          const cfg = stateConfig[node.state];
          const Icon = node.icon ?? Circle;
          const isLast = i === nodes.length - 1;

          return (
            <React.Fragment key={i}>
              {/* Node */}
              <div
                className="bg-white border-[1.5px] rounded-[15px] p-4 shadow-soft min-w-[190px]"
                style={{ borderColor: cfg.border }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-[38px] h-[38px] rounded-[11px] flex-none grid place-items-center"
                    style={{ backgroundColor: cfg.iconBg }}
                  >
                    <Icon className="w-[19px] h-[19px]" style={{ color: cfg.iconColor }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-bold text-[var(--color-text-primary)] truncate">
                      {node.label}
                    </div>
                    {node.sub && (
                      <div className="text-[11px] text-[var(--color-text-tertiary)] truncate">
                        {node.sub}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Connector */}
              {!isLast && (
                <div className="flex items-center flex-none">
                  <div className="w-[42px] h-[2px] bg-[var(--color-border-tertiary)]" />
                  <div
                    className="w-0 h-0"
                    style={{
                      borderTop: "5px solid transparent",
                      borderBottom: "5px solid transparent",
                      borderLeft: "7px solid var(--color-border-tertiary)",
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowPipeline;
